import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Ticket, UtensilsCrossed, User, CreditCard, Clock, ShoppingCart, Receipt, ChefHat } from "lucide-react";
import { CustomerReceipt, KitchenTicket, DeliveryTicket } from "./ReceiptTemplates";
import { useState, useEffect } from "react";
import api from "@/lib/api";

export function OrderDetailDialog({ order, open, onOpenChange }) {
  const sym = "$";
  const [view, setView] = useState("details"); // details, receipt, kitchen
  const [settings, setSettings] = useState({});

  useEffect(() => {
    if (open) {
      setView("details");
      api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }
  }, [open]);

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Ticket className="h-5 w-5 text-[var(--brand-primary)]" />
            {order.ticket_number || `Order ${order.order_id?.slice(-8)}`}
          </DialogTitle>
        </DialogHeader>

        {/* View Tabs */}
        <div className="flex gap-1 border-b border-slate-200 -mx-6 px-6" data-testid="order-view-tabs">
          {[
            { id: "details", label: "Details" },
            { id: "receipt", label: "Receipt" },
            { id: "kitchen", label: "Kitchen Ticket" },
            { id: "delivery", label: "Delivery Ticket" },
          ].map((t) => (
            <button key={t.id} onClick={() => setView(t.id)} className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${view === t.id ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`order-view-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Receipt View */}
        {view === "receipt" && (
          <div className="py-4 flex justify-center bg-slate-50 -mx-6 px-6 rounded-b-lg">
            <CustomerReceipt order={order} settings={settings} />
          </div>
        )}

        {/* Kitchen Ticket View */}
        {view === "kitchen" && (
          <div className="py-4 flex justify-center bg-slate-50 -mx-6 px-6 rounded-b-lg">
            <KitchenTicket order={order} settings={settings} />
          </div>
        )}

        {/* Delivery Ticket View */}
        {view === "delivery" && (
          <div className="py-4 flex justify-center bg-slate-50 -mx-6 px-6 rounded-b-lg">
            <DeliveryTicket order={order} settings={settings} />
          </div>
        )}

        {/* Details View */}
        {view === "details" && (<>

        {/* Order Header */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="order-detail-header">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 font-medium">Ticket #</p>
              <p className="font-mono font-bold text-slate-900">{order.ticket_number || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">Dining Option</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <UtensilsCrossed className="h-3.5 w-3.5 text-slate-500" />
                <span className="font-medium text-slate-700">{order.dining_type_name || "-"}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">Status</p>
              <Badge
                variant={order.status === "completed" ? "default" : order.status === "voided" ? "destructive" : "secondary"}
                className="text-xs capitalize mt-0.5"
              >{order.status}</Badge>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">Created</p>
              <div className="flex items-center gap-1 mt-0.5">
                <Clock className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-700">{order.created_at ? new Date(order.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "-"}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">Employee</p>
              <div className="flex items-center gap-1 mt-0.5">
                <User className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-700">{order.employee_name || "-"}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">Customer</p>
              <span className="text-slate-700">{order.customer_name || "-"}</span>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="order-detail-financials">
          <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-2">
            <Receipt className="h-4 w-4 text-[var(--brand-primary)]" />
            <h3 className="text-sm font-semibold text-slate-900">Financial Summary</h3>
          </div>
          <div className="px-4 py-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Subtotal</p>
                <p className="text-base font-bold text-slate-900">{sym}{(order.subtotal || 0).toFixed(2)}</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Discount</p>
                <p className="text-base font-bold text-emerald-600">
                  {order.discount > 0 ? `-${sym}${order.discount.toFixed(2)}` : `${sym}0.00`}
                </p>
                {order.group_discount_applied > 0 && (
                  <p className="text-[10px] text-emerald-500 mt-0.5">{order.group_discount_applied}% group</p>
                )}
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Tax</p>
                <p className="text-base font-bold text-slate-900">{sym}{(order.tax || 0).toFixed(2)}</p>
              </div>
              <div className="text-center p-3 bg-[var(--brand-primary)] rounded-lg">
                <p className="text-xs text-white/70 mb-1">Final Total</p>
                <p className="text-lg font-bold text-white">{sym}{(order.total || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="order-detail-items">
          <div className="border-b border-slate-100 px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-[var(--brand-primary)]" />
              <h3 className="text-sm font-semibold text-slate-900">Order Items</h3>
            </div>
            <Badge variant="secondary" className="text-xs">{order.items?.length || 0} items</Badge>
          </div>
          <div className="divide-y divide-slate-100">
            {(order.items || []).map((item, idx) => (
              <div key={idx} className="px-4 py-3 flex items-center justify-between" data-testid={`order-detail-item-${idx}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{item.name}</p>
                  <p className="text-xs text-slate-400">{sym}{(item.price || 0).toFixed(2)} x {item.quantity}</p>
                </div>
                <p className="text-sm font-bold text-slate-900 ml-4">{sym}{((item.price || 0) * (item.quantity || 1)).toFixed(2)}</p>
              </div>
            ))}
            {(!order.items || order.items.length === 0) && (
              <div className="px-4 py-6 text-center text-sm text-slate-400">No items recorded</div>
            )}
          </div>
        </div>

        {/* Payment Info */}
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="order-detail-payment">
          <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[var(--brand-primary)]" />
            <h3 className="text-sm font-semibold text-slate-900">Payment</h3>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-xs capitalize font-semibold">{order.payment_method || "cash"}</Badge>
              </div>
              <p className="text-base font-bold text-slate-900">{sym}{(order.total || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="order-detail-notes">
            <p className="text-xs font-semibold text-amber-700 mb-1">Order Notes</p>
            <p className="text-sm text-amber-800">{order.notes}</p>
          </div>
        )}

        </>)}
      </DialogContent>
    </Dialog>
  );
}
